import React, { useState, useEffect, useMemo, useRef } from 'react';

// Pages
import SensitiveDataLandscape from './SensitiveDataLandscape';
import EnterpriseDataLandscape from './EnterpriseDataLandscape';
import DataDiscovery from './DataDiscovery';
import DiscoveryReports from './DiscoveryReports';
import DataSources from './DataSources';
import SamplingRules from './SamplingRules';
import UserSettings from './UserSettings';
import AssetManager from './AssetManager';
import Secure from 'Secure';
import AdminPanel from './AdminPanel';
import CloudAccounts from './CloudAccounts';
import Notification from './Notification';
import Integrations from './Integrations';
import IntegrationEditor from './Integrations/IntegrationEditor';
import { getIntegrations } from './Integrations/api';
import { getAdvancedConfig } from './AdvancedConfig/api';
import { getLicenseStatus, isLicenseActive } from './License/api';
import Drivers from './Drivers';
import Metadata from './Metadata';
import DataSourceDetail from './Metadata/DataSourceDetail';
import FolderDetail from './Metadata/FolderDetail';
import TableDetail from './Metadata/TableDetail';
import SchemaLibraryScreen from './Metadata/SchemaLibraryScreen';
import InventoryManager from './Secure/InventoryManager';
import InventorySourcePage from './Secure/InventoryManager/InventorySourcePage';

// Editors
import SourceEditor from './DataSources/SourceEditor';
import DataSourcePicker from './DataSources/DataSourcePicker';
import RulesEditor from './SamplingRules/RulesEditor';

// Components
import Navigation from './Navigation';
import RequireAdmin from './Auth/RequireAdmin';
import LicenseDialog from './LicenseDialog';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Container, Spinner } from 'react-bootstrap';
import { Link, Switch, Route, useLocation, useHistory } from 'react-router-dom';
import { API } from './Auth';
import {
  countDiscoveryDocsWithMissingFields,
  countTableDocsWithMissingFields,
  getDataSources,
  getRulesList
} from './api';
import {
  getHistory,
  mapHistoryRunsToSearchStatuses,
  startDiscovery,
  buildDiscoveryRequest,
  enrichRunsWithJobStatus,
  deleteFailedRunsForDataSource,
} from './DataDiscovery/discoverApi';
import { getDataSourceNumericId } from './DataSources/datasourcesApi';
import { ensureConnectionTested } from './Utils/connectionGate';
import { 
  checkForLicenseIssue,
  getConnectionsInProgress,
  getErrorMessage,  
  getHostToConnectionsMap,
  getRulesMap,
  getSearchesInProgress
} from 'Utils';
import { usePolling } from 'Hooks';
import defaultRules from './SamplingRules/RulesEditor/default-rules';
import AppContext from 'Utils/AppContext';
import './App.css';
import '../node_modules/react-vis/dist/style.css';
import 'react-perfect-scrollbar/dist/css/styles.css';
// import { FaRegThumbsUp } from 'react-icons/fa';
// Test comment for hot reload
process.env.NODE_TLS_REJECT_UNAUTHORIZED = "0";


// How long an optimistic (locally added) run row may live before it's pruned even if the
// fetched history never showed a matching real run — a safety net against phantom rows
// when a start succeeded client-side but the run vanished server-side.
const OPTIMISTIC_RUN_MAX_AGE_MS = 5 * 60 * 1000;

// A fetched run row supersedes an optimistic placeholder when the label matches, or when
// it's a run on the same data source started at (or after) the optimistic row's creation.
// The 60s slack absorbs client/server clock skew on start_date.
const realRunMatchesOptimistic = (real, optimistic) => {
  if (optimistic.searchName && real.searchName === optimistic.searchName) return true;
  return real.dataSourceId != null && optimistic.dataSourceId != null &&
    Number(real.dataSourceId) === Number(optimistic.dataSourceId) &&
    (real.creationTime ?? 0) >= optimistic.creationTime - 60000;
};

// Reset the main scroll container to the top whenever the route changes.
// PerfectScrollbar manages its own scroll position, so window.scrollTo is a no-op here.
function ScrollToTop({ scrollContainerRef }) {
  const { pathname } = useLocation();
  useEffect(() => {
    const node = scrollContainerRef.current;
    if (node) {
      node.scrollTop = 0;
      node.scrollLeft = 0;
    }
  }, [pathname, scrollContainerRef]);
  return null;
}

function App(props) {
  const history = useHistory();
  const [rulesList, setRulesList] = useState([defaultRules]);
  const [connections, setConnections] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  // Install-wide entity-level config (set in the hidden Advanced Settings app). Loaded once on
  // boot for EVERY user so their reports/exports honor what the operator configured.
  const [entityLevels, setEntityLevels] = useState({});
  // Install-wide "most important PII first" ordering for entity-type displays (set in the hidden
  // Advanced Settings app). Empty → consumers apply the shipped default order (entityPriority.js).
  const [entityOrder, setEntityOrder] = useState([]);
  // Runtime toggle for the Profile / Schema Metadata feature, loaded from advanced-config on
  // boot (replaces the old build-time REACT_APP_PROFILE_ENABLED). Drives the nav-link gate.
  // Defaults ON — the Schema Metadata feature is live (backend now in c2-discover-api). The
  // boot config load below still lets an admin's explicit OFF win.
  const [profileEnabled, setProfileEnabled] = useState(true);
  // Data-source Import/Export is gated by an admin-flipped advanced-config flag (C2D-1000/998);
  // default OFF until the boot config load says otherwise.
  const [importExportEnabled, setImportExportEnabled] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [searchStatuses, setSearchStatuses] = useState([]);
  const [notification, setNotification] = useState(null);
  const [locallyStartedSourceIds, setLocallyStartedSourceIds] = useState([]);
  // Placeholder rows shown in Review Prior Discovery the moment a discovery is started,
  // until a poll of the run history returns the real run. Never merged into
  // searchStatuses state — combined at render time in displayedSearchStatuses.
  const [optimisticRuns, setOptimisticRuns] = useState([]);
  const landscapeData = { sources: [] };
  const unscannedSources = { sources: [] };

  // License hooks
  const [licenseIssue, setLicenseIssue] = useState(false);
  const [licenseData, setLicenseData] = useState({});
  // Deployment cloud identifier from the API (aws | azure | standalone). Fetched once at
  // boot; used by cloud-specific forms to filter irrelevant options.
  const [deploymentCloud, setDeploymentCloud] = useState('standalone');
  // Which IAM/Entra auth modes the data-source form should offer. Computed server-side
  // from the deployment cloud + registered Cloud Accounts, so non-admins get the right
  // filter without needing read access to the admin cloud-accounts endpoint.
  const [availableAuthClouds, setAvailableAuthClouds] = useState([]);
  const rulesMap = useMemo(() => getRulesMap(rulesList), [rulesList]);
  const scrollContainerRef = useRef(null);

  // What the UI renders: optimistic placeholders (not yet superseded by a fetched run)
  // on top of the fetched history.
  const displayedSearchStatuses = useMemo(() => {
    const pending = optimisticRuns.filter(
      (o) => !searchStatuses.some((s) => realRunMatchesOptimistic(s, o))
    );
    return pending.length ? pending.concat(searchStatuses) : searchStatuses;
  }, [optimisticRuns, searchStatuses]);

  const connectionsInProgress = getConnectionsInProgress(displayedSearchStatuses);
  const hostToConnectionsMap = getHostToConnectionsMap(connections);
  const searchesInProgress = useMemo(() => getSearchesInProgress(searchStatuses), [searchStatuses]);

  // Single boot routine for both the initial mount and a post-license-apply reload.
  // Keep these two callers pointed at the SAME function — the previous split (a separate
  // init() that only ran on license-apply) is what let the deployment-config fetch drift
  // out of the normal mount path, hiding the AWS IAM / Azure Entra auth modes.
  const loadApp = async () => {
    setFetchingData(true);
    try {
      // Deployment cloud + available auth clouds. Drives which auth modes the data-source
      // editor offers. Failure is non-fatal: the UI keeps the "standalone" default (all
      // options shown).
      try {
        const deployment = await API.get('api', '/api/config/deployment');
        if (deployment && deployment.cloud) {
          setDeploymentCloud(deployment.cloud);
        }
        if (deployment && Array.isArray(deployment.availableAuthClouds)) {
          setAvailableAuthClouds(deployment.availableAuthClouds);
        }
      } catch (e) {
        console.log('deployment config fetch failed; defaulting to standalone', e);
      }

      await Promise.all([
        getLicenseUsage(),
        loadSearchStatusesHandler(),
        loadRulesHandler(),
        loadConnectionsHandler(),
        loadIntegrationsHandler(),
        loadEntityLevelsHandler(),
        countDiscoveryDocsWithMissingFields(),
        countTableDocsWithMissingFields()
      ]).catch((e) => {
        const licenseIssue = checkForLicenseIssue(e);
        if (licenseIssue) {
          setLicenseIssue(true);
          setLicenseData(licenseIssue);
        }
        throw e;
      });

    } catch(e) {
      console.log(e);
      console.log(e.response);
    }
    setFetchingData(false);
  }

  const startSearchHandler = async (connection, rules, name) => {
    try {
      const gate = await ensureConnectionTested(connection, { history, setNotification });
      if (!gate.ok) return;
      const dataSourceId = getDataSourceNumericId(connection);
      if (dataSourceId != null) {
        await deleteFailedRunsForDataSource(dataSourceId);
      }
      const request = buildDiscoveryRequest(connection, rules, name);
      await startDiscovery(request);
      addOptimisticRun({
        searchName: name,
        connection: connection?.name,
        type: connection?.type,
        dataSourceId,
        sampleRules: rules?.name,
      });
      const header = 'Discovery Status';
      const message = <span>Successfully kicked off Discovery for <Link to='/discover'>{name}</Link></span>;
      await loadSearchStatusesHandler();
      await loadConnectionsHandler();
      setNotification({ header, body: message });
    } catch (e) {
      console.log(e);
      const errorMsg = getErrorMessage(e);
      if (errorMsg?.message) {
        setNotification({ header: `Error running Discovery on ${connection.name}`, body: errorMsg.message });
      }
    }
  };

  const getLicenseUsage = async () => {
    try {
      const response = await getLicenseStatus();
      if (response) {
        setLicenseData(response);
        if (!isLicenseActive(response)) {
          setLicenseIssue(true);
        }
      }
    } catch (e) {
      console.log('license status fetch failed', e);
    }
  }

  const closeModalHandler = () => {
    setLicenseIssue(false);
  }

  const onLicenseApplied = async () => {
    setLicenseIssue(false);
    await loadApp();
  }

  useEffect(() => {
    loadApp();
  }, []);

  const loadRulesHandler = async () => {
    let rulesListValue = [];
    try {
      const response = await getRulesList();
      if (response) {
        rulesListValue = response;
      }
    } catch(e) {
      console.log(e);
      throw e;
    }
    setRulesList(rulesListValue);
  }

  const loadConnectionsHandler = async () => {
    let connectionsValue = [];
    try {
      const response = await getDataSources();
      if (response) connectionsValue = response;
    } catch(e) {
      console.log(e);
      throw e;
    }
    setConnections(connectionsValue);
  }

  const loadEntityLevelsHandler = async () => {
    // Best-effort: entity-level config is an optional refinement. If the endpoint is
    // unavailable (older API, network hiccup), fall back to "no config" (everything NORMAL)
    // rather than blocking app boot.
    try {
      const cfg = await getAdvancedConfig();
      setEntityLevels(cfg?.entityLevels || {});
      setEntityOrder(Array.isArray(cfg?.entityOrder) ? cfg.entityOrder : []);
      // Default-on: only an explicit false from config hides the nav item.
      setProfileEnabled(cfg?.profileEnabled !== false);
      // Default-off: the Import/Export controls appear only on an explicit true (C2D-1000/998).
      setImportExportEnabled(cfg?.dataSourceImportExportEnabled === true);
    } catch (e) {
      console.log('entity-level config load failed; defaulting to NORMAL for all entities', e);
      setEntityLevels({});
      setEntityOrder([]);
      setProfileEnabled(true);
    }
  }

  const loadIntegrationsHandler = async () => {
    let integrationsValue = [];
    try {
      const response = await getIntegrations();
      if (response) integrationsValue = response;
    } catch(e) {
      console.log(e);
      throw e;
    }
    setIntegrations(integrationsValue);
  }

  const loadSearchStatusesHandler = async () => {
    let statuses = [];
    try {
      const response = await getHistory(0, 20);
      let runs = response?.runs ?? [];
      runs = await enrichRunsWithJobStatus(runs);
      statuses = mapHistoryRunsToSearchStatuses(runs);
    } catch (e) {
      console.log(e);
      return;
    }
    // Drop optimistic placeholders the fetched history now covers, plus any that aged out.
    setOptimisticRuns((prev) => prev.filter((o) =>
      Date.now() - o.creationTime < OPTIMISTIC_RUN_MAX_AGE_MS &&
      !statuses.some((s) => realRunMatchesOptimistic(s, o))
    ));
    setSearchStatuses((prevStatuses) => {
      if (prevStatuses && prevStatuses.length) {
        const prevStatusesMap = getSearchesInProgress(prevStatuses);
        const currStatusesMap = getSearchesInProgress(statuses);
        for (const status in prevStatusesMap) {
          if (!currStatusesMap[status]) {
            const searchName = prevStatusesMap[status].searchName ? prevStatusesMap[status].searchName : prevStatusesMap[status].searchid;
            setNotification({
              header: 'Discovery Complete',
              body: <span>Completed discovery for <Link to={`/results/${status}`}>{searchName}</Link>.</span>
            });
          }
        }
      }
      return statuses;
    });
  };

  // const contextMap = useMemo(() => ({ 
  //   rulesList: rulesList,
  //   connections: connections,
  //   defaultRules: rulesList[0],
  //   loadConnectionsHandler: loadConnectionsHandler,
  //   startSearchHandler: startSearchHandler,
  //   fetchNewEntitlements: getLicenseUsage,
  //   hostToConnectionsMap: hostToConnectionsMap,
  //   setNotification: setNotification,
  //   usedConnections: usedConnections,
  //   maxConnections: maxConnections
  // }), [rulesList, connections]);

  // Poll to update Review Prior Discovery: fetch history + job status for RUNNING runs, then setSearchStatuses.
  usePolling(async () => {
    await loadSearchStatusesHandler();
  }, 30000);

  const markDiscoveryStarted = (sourceId) => {
    const id = Number(sourceId);
    setLocallyStartedSourceIds((prev) => prev.indexOf(id) >= 0 ? prev : prev.concat([id]));
  };

  // Add a QUEUED placeholder row to Review Prior Discovery immediately after a start call
  // succeeds. No run_id/jobId on purpose, and the name cell skips the results link for
  // optimistic rows — nothing downstream can mistake it for a real run.
  const addOptimisticRun = ({ searchName, connection, type, dataSourceId, sampleRules }) => {
    const now = Date.now();
    // searchid must be set (and unique): material-table's parentChildData matches
    // parent rows by searchid, and an undefined searchid would adopt every
    // parentless row. The pending- prefix never collides with real numeric run ids.
    setOptimisticRuns((prev) => prev.concat([{
      optimistic: true,
      searchid: `pending-${now}-${prev.length}`,
      glue_status: 'QUEUED',
      state: 'QUEUED',
      creationTime: now,
      last_updated: now,
      searchName,
      connection,
      type,
      dataSourceId: dataSourceId != null ? Number(dataSourceId) : undefined,
      sampleRules: sampleRules || '-',
    }]));
  };

  const contextMap = {
    rulesList: rulesList,
    connections: connections,
    integrations: integrations,
    entityLevels: entityLevels,
    entityOrder: entityOrder,
    profileEnabled: profileEnabled,
    dataSourceImportExportEnabled: importExportEnabled,
    defaultRules: rulesList[0],
    loadConnectionsHandler: loadConnectionsHandler,
    loadIntegrationsHandler: loadIntegrationsHandler,
    loadSearchStatusesHandler: loadSearchStatusesHandler,
    startSearchHandler: startSearchHandler,
    fetchNewEntitlements: getLicenseUsage,
    hostToConnectionsMap: hostToConnectionsMap,
    setNotification: setNotification,
    rulesMap: rulesMap,
    deploymentCloud: deploymentCloud,
    availableAuthClouds: availableAuthClouds,
    searchStatuses: displayedSearchStatuses,
    locallyStartedSourceIds: locallyStartedSourceIds,
    markDiscoveryStarted: markDiscoveryStarted,
    addOptimisticRun: addOptimisticRun,
    isAdmin: props.isAdmin,
  };
  
  return (
    <div className='App mr-0'>
      { fetchingData ? (
        <Container>
          <div 
            className='spinner-container'
          >
            <Spinner animation='grow' variant='primary' />
          </div>
        </Container>
      ) : (
        <div className='d-flex'>
          <LicenseDialog
            config={props.config}
            licenseIssue={licenseIssue}
            closeModalHandler={closeModalHandler}
            onLicenseApplied={onLicenseApplied}
            licenseData={licenseData}
          />
          <Navigation
            user={props.user}
            isAdmin={props.isAdmin}
            profileEnabled={profileEnabled}
          />
          <div role='main' className='main-content'>
            <AppContext.Provider value={contextMap}>
              <PerfectScrollbar containerRef={(el) => { scrollContainerRef.current = el; }}>
                <ScrollToTop scrollContainerRef={scrollContainerRef} />
                <Switch>
                  <Route exact path='/discover'>
                    <DataDiscovery
                      startSearch={async (connection, rules, name) => await startSearchHandler(connection, rules,name )}
                      connections={connections}
                      rulesList={rulesList}
                      fetchingData={fetchingData}
                      searchStatuses={displayedSearchStatuses}
                      connectionsInProgress={connectionsInProgress}
                    />
                  </Route>
                  <Route exact path='/sources'>
                    <DataSources
                      fetchingData={fetchingData}
                      connections={connections}
                      loadConnectionsHandler={async () => await loadConnectionsHandler()}
                      fetchNewEntitlements={async () => await getLicenseUsage()}
                    />
                  </Route>
                  {/* New-X picker (?action=discover|profile|mask) — must precede /sources/source-editor/:id. */}
                  <Route exact path='/sources/pick'>
                    <DataSourcePicker />
                  </Route>
                    <Route name='integration_editor' exact path='/integrations/editor'>
                      <IntegrationEditor   />
                    </Route>
                    <Route name='integration_editor' exact path='/integrations/editor/:id'>
                      <IntegrationEditor  />
                    </Route>
                  <Route exact path='/sources/source-editor'>
                    <SourceEditor
                      rulesList={rulesList}
                      connections={connections}
                      loadConnectionsHandler={async () => await loadConnectionsHandler()}
                      fetchNewEntitlements={async () => await getLicenseUsage()}
                    />
                  </Route>
                  <Route path='/sources/source-editor/:id'>
                    <SourceEditor
                      rulesList={rulesList}
                      connections={connections}
                      loadConnectionsHandler={async () => await loadConnectionsHandler()}
                      fetchNewEntitlements={async () => await getLicenseUsage()}
                    />
                  </Route>
                  <Route exact path='/rules'>
                    <SamplingRules rulesList={rulesList} />
                  </Route>
                  <Route exact path='/rules/rules-editor'>
                    <RulesEditor
                      fetchNewRules={async () => await loadRulesHandler()}
                      rulesList={rulesList}
                    />
                  </Route>
                  <Route exact path='/rules/rules-editor/:id'>
                    <RulesEditor
                      fetchNewRules={async () => await loadRulesHandler()}
                      rulesList={rulesList}
                    />
                  </Route>
                  <Route path='/manage'>
                    <AssetManager
                      connections={connections}
                    />
                  </Route>                        
                    <Route path='/integrations'>
                      <RequireAdmin isAdmin={props.isAdmin}>
                        <Integrations
                        />
                      </RequireAdmin>
                    </Route>
                  <Route path='/secure'>
                    <Secure />
                  </Route>
                  <Route path='/results'>
                    <DiscoveryReports searchesInProgress={searchesInProgress} connections={connections} />
                  </Route>
                  <Route exact path='/metadata'>
                    <Metadata />
                  </Route>
                  {/* Static /metadata/library must precede the :identityId catch-all below,
                      else RRv5 matches "library" as an identityId. */}
                  <Route exact path='/metadata/library'>
                    <SchemaLibraryScreen isAdmin={props.isAdmin} />
                  </Route>
                  <Route exact path='/metadata/source/:dataSourceId'>
                    <DataSourceDetail isAdmin={props.isAdmin} />
                  </Route>
                  {/* :folderPath+ preserves slashes (RRv5); must precede the :identityId catch-all. */}
                  <Route exact path='/metadata/source/:dataSourceId/folder/:folderPath+'>
                    <FolderDetail />
                  </Route>
                  <Route exact path='/metadata/source/:dataSourceId/table/:tableName'>
                    <TableDetail />
                  </Route>
                  <Route exact path='/metadata/:identityId'>
                    <Metadata />
                  </Route>
                  <Route exact path='/inventory-manager'>
                    <InventoryManager />
                  </Route>
                  <Route exact path='/inventory-manager/:id'>
                    <InventorySourcePage />
                  </Route>
                  <Route path='/admin'>
                    <RequireAdmin isAdmin={props.isAdmin}>
                      <AdminPanel config={props.config} />
                    </RequireAdmin>
                  </Route>
                  <Route path='/cloud-accounts'>
                    <RequireAdmin isAdmin={props.isAdmin}>
                      <CloudAccounts />
                    </RequireAdmin>
                  </Route>
                  <Route path='/user'>
                    <UserSettings
                      config={props.config}
                      licenseData={licenseData}
                      connections={connections}
                      user={props.user}
                    />
                  </Route>
                  <Route path='/drivers'>
                    <Drivers isAdmin={props.isAdmin} />
                  </Route>
                  <Route path='/enterprise'>
                    <EnterpriseDataLandscape />
                  </Route>
                  <Route path='/landscape'>
                    <SensitiveDataLandscape
                      landscapeData={landscapeData}
                      unscannedSources={unscannedSources}
                    />
                  </Route>
                  <Route exact path='/'>
                    <SensitiveDataLandscape
                      landscapeData={landscapeData}
                      unscannedSources={unscannedSources}
                    />
                  </Route>
                </Switch>
              </PerfectScrollbar>
            </AppContext.Provider>
          </div>
          { notification && 
            <Notification 
              notification={notification}
              setNotification={(val) => setNotification(val)}
            />
          }

        </div>
      )}
    </div>
  );
}

export default App;